import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, FileSpreadsheet, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { useToast } from './ui/use-toast';
import { useIsMobile } from '../hooks/use-mobile';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useCompanyState } from '../hooks/useCompanyState';
import { AppBar } from './ui/app-bar';
import * as XLSX from 'xlsx';
import { cn } from '@/lib/utils';
import SerialNumberEntryModal from './SerialNumberEntryModal';
import ProductEntryForm from './ProductEntryForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { getBaseUrl } from '@/lib/api';
import { useSettings } from '@/hooks/useSettings';

interface SerialNumber {
  id: string;
  partNo: string;
  spIncGST: number;
  costPrice?: number;
  batchNo: string;
  mfgDate: string;
  expiryDays: string;
  auto: boolean;
  forever: boolean;
}

interface Product {
  id: string;
  productName: string;
  barcode: string;
  code: number;
  hsnCode: string;
  unitCost: number;
  quantity: number;
  discountPer: number;
  discountAmount: number;
  sgstPer: number;
  sgstAmount: number;
  cgstPer: number;
  cgstAmount: number;
  igstPer: number;
  igstAmount: number;
  cessPer: number;
  cessAmount: number;
  totalCost: number;
  availableQuantity: number;
  invoiceQuantity: number;
  partNo: string;
  indentNo?: string;
  hasSerial?: boolean;
  serialNumbers?: SerialNumber[];
  // Additional fields from supplier quantities API
  soldQty?: number;
  discount?: number;
  sgst?: number;
  cgst?: number;
  cess?: number;
  igst?: number;
  returnQuantity?: number;
  serialDetails?: Array<{
    SerialNumber: string;
    Quantity: number;
    Rate: number;
  }>;
}

interface SupplierInvoiceProductEntryProps {
  token: string;
  invoiceId?: number;
  invoiceRefNo?: string;
  supplierName?: string;
  supplierId?: number;
  locationId?: number;
  type?: string; // Type: 'Invoice' or 'Debit Note'
  onBack?: () => void;
}

const SupplierInvoiceProductEntry: React.FC<SupplierInvoiceProductEntryProps> = ({
  token,
  invoiceId,
  invoiceRefNo,
  supplierName,
  supplierId,
  locationId,
  type,
  onBack,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Get invoice data from location state if available
  const invoiceData = location.state as {
    invoiceId?: number;
    invoiceRefNo?: string;
    supplierName?: string;
    supplierId?: number;
    locationId?: number;
    supplierState?: string;
    fromInvoicesPayments?: boolean;
    backToSupplierIds?: number[];
    backToSupplierState?: string;
    type?: string;
  } | null;

  const finalInvoiceId = invoiceId || invoiceData?.invoiceId;
  const finalInvoiceRefNo = invoiceRefNo || invoiceData?.invoiceRefNo || 'XX';
  const finalSupplierName = supplierName || invoiceData?.supplierName || 'Supplier';
  const finalSupplierId = supplierId || invoiceData?.supplierId;
  const finalLocationId = locationId || invoiceData?.locationId;
  const finalType = type || invoiceData?.type || 'Invoice';
  const fromInvoicesPayments = invoiceData?.fromInvoicesPayments || false;
  const backToSupplierIds = invoiceData?.backToSupplierIds || [];
  const backToSupplierState = invoiceData?.backToSupplierState;
  
  // Determine if this is a debit note
  const isDebitNote = String(finalType) === '1' || String(finalType).toLowerCase() === 'debit note';

  const { companyState } = useCompanyState();
  const defaultState = companyState || 'Telangana';
  const finalSupplierState = (invoiceData?.supplierState ?? defaultState).trim() || defaultState;
  const isSameState = !!companyState && finalSupplierState.toLowerCase() === companyState.toLowerCase();

  // Debug: Log received location information
  console.log('\n📍 ═══════════════════════════════════════════════════════════');
  console.log('📍 SUPPLIER INVOICE PRODUCT ENTRY - LOCATION DEBUG');
  console.log('📍 ═══════════════════════════════════════════════════════════');
  console.log('📥 Props Received:');
  console.log('   ├─ invoiceId (prop):', invoiceId);
  console.log('   ├─ invoiceRefNo (prop):', invoiceRefNo);
  console.log('   ├─ supplierName (prop):', supplierName);
  console.log('   ├─ supplierId (prop):', supplierId);
  console.log('   ├─ locationId (prop):', locationId);
  console.log('   ├─ type (prop):', type);
  console.log('📍 Location State Received:');
  console.log(JSON.stringify(invoiceData, null, 2));
  console.log('🎯 Final Values Being Used:');
  console.log('   ├─ Final Invoice ID:', finalInvoiceId);
  console.log('   ├─ Final Invoice Ref No:', finalInvoiceRefNo);
  console.log('   ├─ Final Supplier Name:', finalSupplierName);
  console.log('   ├─ Final Supplier ID:', finalSupplierId);
  console.log('   ├─ Final Location ID:', finalLocationId);
  console.log('   ├─ Final Type:', finalType);
  console.log('   ├─ Is Debit Note:', isDebitNote);
  console.log('   ├─ Company State (Settings):', companyState);
  console.log('   ├─ Final Supplier State:', finalSupplierState, '(isSameState:', isSameState + ')');
  console.log('   ├─ From Invoices Payments:', fromInvoicesPayments);
  console.log('   ├─ Back To Supplier IDs:', backToSupplierIds);
  console.log('   └─ Back To Supplier State:', backToSupplierState);
  console.log('📍 ═══════════════════════════════════════════════════════════\n');

  // Form state
  const [indentNo, setIndentNo] = useState('');

  // Products state
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [rawInputValues, setRawInputValues] = useState<{ [key: string]: string }>({});
  const [sellNonGST, setSellNonGST] = useState(false);

  // Overwrite options state
  const [overwriteOption, setOverwriteOption] = useState<'overwrite' | 'average' | 'maximum'>('average');

  // Product search state
  const [productNameSearchTerm, setProductNameSearchTerm] = useState('');
  const [barcodeSearchTerm, setBarcodeSearchTerm] = useState('');
  const [debouncedProductNameSearch, setDebouncedProductNameSearch] = useState('');
  const [debouncedBarcodeSearch, setDebouncedBarcodeSearch] = useState('');
  const [showProductNameDropdown, setShowProductNameDropdown] = useState(false);
  const [showBarcodeDropdown, setShowBarcodeDropdown] = useState(false);
  const [productNameDropdownActiveIndex, setProductNameDropdownActiveIndex] = useState(-1);
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const barcodeDropdownRef = useRef<HTMLDivElement>(null);

  // Serial number modal state
  const [isSerialModalOpen, setIsSerialModalOpen] = useState(false);
  const [currentProductForSerial, setCurrentProductForSerial] = useState<any>(null);

  // Add New Product modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const productFormRef = useRef<any>(null);

  // Serial number popup state for deleted serial numbers (single vs multiple)
  const [showSerialPopup, setShowSerialPopup] = useState(false);
  const [deletedSerialInfo, setDeletedSerialInfo] = useState<{
    productId: number;
    serialNumber: string;
    locationId: number;
  } | null>(null);
  const [deletedSerials, setDeletedSerials] = useState<Array<{
    productId: number;
    serialNumber: string;
    locationId: number;
  }>>([]);
  const [serialFormData, setSerialFormData] = useState({
    batchNo: '0',
    mrp: '0',
    rate: '0',
    tag1: '',
    tag2: '',
    mfgDate: new Date().toISOString().split('T')[0],
    expiryDays: '365',
    forever: false, // Will be updated from settings when popup opens
  });
  const [serialTableFormData, setSerialTableFormData] = useState<Array<{
    mfgDate: string;
    batchNo: string;
    rate: string;
    costPrice: string;
    forever: boolean;
    expiryDays: string;
  }>>([]);
  const [isSubmittingSerials, setIsSubmittingSerials] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const { getBooleanSetting, settings } = useSettings();
  const expiryForeverDefault = settings.length > 0 ? getBooleanSetting('Expiry forever') : true;

  // Debounce search terms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductNameSearch(productNameSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [productNameSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBarcodeSearch(barcodeSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [barcodeSearchTerm]);

  // Back navigation handler
  const handleBack = useCallback(() => {
    console.log('\n🔙 ═══════════════════════════════════════════════════════════');
    console.log('🔙 BACK NAVIGATION FROM PRODUCT ENTRY');
    console.log('🔙 ═══════════════════════════════════════════════════════════');
    console.log('📋 Navigation Options:');
    console.log('   ├─ onBack provided:', !!onBack);
    console.log('   ├─ fromInvoicesPayments:', fromInvoicesPayments);
    console.log('   ├─ Has location state:', !!location.state);
    console.log('   ├─ Back To Supplier IDs:', backToSupplierIds);
    console.log('   └─ Navigation method chosen:', onBack ? 'onBack callback' : 'navigate based on source');
    console.log('🔙 ═══════════════════════════════════════════════════════════\n');

    if (onBack) {
      onBack();
    } else if (fromInvoicesPayments && backToSupplierIds.length > 0) {
      // If we came from invoices & payments, navigate back to the invoices & payments page with supplier context
      console.log('🔙 Navigating back to invoices & payments page with supplier context');
      queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      navigate('/supplier-invoices-payments', {
        state: {
          supplierIds: backToSupplierIds,
          supplierState: backToSupplierState,
        },
        replace: true,
      });
    } else {
      // Default fallback - go back in history
      navigate(-1);
    }
  }, [onBack, navigate, fromInvoicesPayments, backToSupplierIds, backToSupplierState, queryClient]);

  useBackNavigation({
    onBack: handleBack,
    canGoBack: true,
    showExitConfirmation: false,
  });

  // Fetch products function - using the specified API endpoint
  const fetchProductsFiltered = async (search: string) => {
    try {
      // Use the specified API endpoint with the invoice's location ID
      const locationIdToUse = finalLocationId || 'all'; // Fallback to 'all' if no location ID
      const apiUrl = `${baseUrl}/api/Products?PageStart=0&MaxRecords=200&LocationID=${locationIdToUse}&CategoryID=all&FilterValue=${encodeURIComponent(search)}`;
      
      console.log('\n🔍 ═══════════════════════════════════════════════════════════');
      console.log('🔍 PRODUCTS API CALL WITH LOCATION');
      console.log('🔍 ═══════════════════════════════════════════════════════════');
      console.log('🔎 Search Parameters:');
      console.log('   ├─ Search Term:', search);
      console.log('   ├─ Final Location ID:', finalLocationId);
      console.log('   ├─ Location ID Being Used:', locationIdToUse);
      console.log('   └─ Base URL:', baseUrl);
      console.log('🌐 Complete API URL:');
      console.log('   ', apiUrl);
      console.log('📤 Request Headers:');
      console.log('   ├─ Authorization:', authToken.substring(0, 30) + '...');
      console.log('   └─ Content-Type: application/json');
      console.log('🔍 ═══════════════════════════════════════════════════════════\n');

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('\n📥 ═══════════════════════════════════════════════════════════');
      console.log('📥 PRODUCTS API RESPONSE');
      console.log('📥 ═══════════════════════════════════════════════════════════');
      console.log('📊 Response Status:', response.status, response.statusText);
      console.log('📊 Response Headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Error Response Body:', errorText);
        console.log('📥 ═══════════════════════════════════════════════════════════\n');
        throw new Error(`Failed to fetch products: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Success Response Data:');
      console.log(JSON.stringify(data, null, 2));
      console.log('📥 ═══════════════════════════════════════════════════════════\n');
      
      return normalizeProductData(data);
    } catch (error) {
      console.error('\n💥 ═══════════════════════════════════════════════════════════');
      console.log('💥 PRODUCTS API ERROR');
      console.log('💥 ═══════════════════════════════════════════════════════════');
      console.error('💥 Error Details:', error);
      console.log('💥 ═══════════════════════════════════════════════════════════\n');
      throw error;
    }
  };

  // Helper to parse percentage from API (number or string)
  const parsePercent = (v: any, fallback = 0): number => {
    if (v == null || v === '') return fallback;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isNaN(n) ? fallback : n;
  };

  // Helper function to normalize product data
  const normalizeProductData = (data: any) => {
    // Normalize data keys to match SalesInvoice pattern
    if (Array.isArray(data.data)) {
      data.data = data.data.map((item: any) => ({
        ...item,
        Title: item.title || item.Title || item.ProductName || '',
        Barcode: item.barcode || item.Barcode || '',
        Code: item.code || item.Code || 0,
        Category: item.Category || item.category || '',
        HSN: item.HSN || item.hsn || item.HSNCode || '',
        MRP: item.rate || item.MRP || item.mrp || 0,
        CostPrice: item.costprice || item.CostPrice || item.UnitCost || 0,
        UQCName: item.UQCName || item.uqcname || '',
        BrandName: item.BrandName || item.brandname || '',
        Available: item.available || item.Available || 0,
        OfferPrice: item.OfferPrice || item.offerprice || 0,
        CessPer: parsePercent(item.CessPer ?? item.cessper ?? item.Cess, 0),
        SgstPer: parsePercent(item.SgstPer ?? item.sgstper, 0),
        CGstPer: parsePercent(item.CGstPer ?? item.cgstper, 0),
        IgstPer: parsePercent(item.IgstPer ?? item.igstper, 0),
        DiscountPer: item.DiscountPer != null ? String(item.DiscountPer) : (item.discountper != null ? String(item.discountper) : '0'),
        GST: item.gstper !== undefined && item.gstper !== null ? item.gstper : (item.GST || item.gst || 0),
        isTraceable: item.isTraceable || false,
        imgstr: item.imgstr || item.image || '/placeholder.svg',
        hasserial: item.hasserial || item.hasserial || item.hasSerial || 0,
      }));
    }
    return data;
  };

  // Fetch products for product name autocomplete
  const { data: productNameResponse, isLoading: isProductNameLoading } = useQuery({
    queryKey: ['products-name', debouncedProductNameSearch, finalLocationId],
    queryFn: () => fetchProductsFiltered(debouncedProductNameSearch),
    enabled: debouncedProductNameSearch.length > 0 && finalLocationId !== undefined,
    staleTime: 0, // Don't use stale data - always fetch fresh
    gcTime: 1000 * 60 * 2, // Reduce cache time to 2 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const productNameProducts = productNameResponse?.data || [];

  useEffect(() => {
    if (showProductNameDropdown && productNameProducts.length > 0) {
      setProductNameDropdownActiveIndex(0);
    } else {
      setProductNameDropdownActiveIndex(-1);
    }
  }, [showProductNameDropdown, productNameProducts]);

  useEffect(() => {
    if (productNameDropdownActiveIndex < 0) return;
    const items = productDropdownRef.current?.querySelectorAll('.product-name-dropdown-item');
    const el = items?.[productNameDropdownActiveIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [productNameDropdownActiveIndex]);

  // Fetch products for barcode autocomplete
  const { data: barcodeProductsResponse, isLoading: isBarcodeLoading } = useQuery({
    queryKey: ['products-barcode', debouncedBarcodeSearch, finalLocationId],
    queryFn: () => fetchProductsFiltered(debouncedBarcodeSearch),
    enabled: debouncedBarcodeSearch.length > 0 && finalLocationId !== undefined,
    staleTime: 0, // Don't use stale data - always fetch fresh
    gcTime: 1000 * 60 * 2, // Reduce cache time to 2 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const barcodeProducts = barcodeProductsResponse?.data || [];

  // Debug: Log query state changes
  console.log('\n🔄 ═══════════════════════════════════════════════════════════');
  console.log('🔄 QUERY STATE DEBUG');
  console.log('🔄 ═══════════════════════════════════════════════════════════');
  console.log('🔍 Product Name Query:');
  console.log('   ├─ Query Key:', ['products-name', debouncedProductNameSearch, finalLocationId]);
  console.log('   ├─ Enabled:', debouncedProductNameSearch.length > 0 && finalLocationId !== undefined);
  console.log('   ├─ Search Term:', debouncedProductNameSearch);
  console.log('   ├─ Location ID:', finalLocationId);
  console.log('   ├─ Loading:', isProductNameLoading);
  console.log('   └─ Results Count:', productNameProducts.length);
  console.log('🔍 Barcode Query:');
  console.log('   ├─ Query Key:', ['products-barcode', debouncedBarcodeSearch, finalLocationId]);
  console.log('   ├─ Enabled:', debouncedBarcodeSearch.length > 0 && finalLocationId !== undefined);
  console.log('   ├─ Search Term:', debouncedBarcodeSearch);
  console.log('   ├─ Location ID:', finalLocationId);
  console.log('   ├─ Loading:', isBarcodeLoading);
  console.log('   └─ Results Count:', barcodeProducts.length);
  console.log('🔄 ═══════════════════════════════════════════════════════════\n');

  // Fetch existing supplier quantities for the invoice
  const fetchSupplierQuantities = async () => {
    if (!finalInvoiceId || !finalLocationId) {
      console.log('⚠️ Missing invoiceId or locationId for fetching supplier quantities');
      return;
    }

    try {
      console.log('\n📊 ═══════════════════════════════════════════════════════════');
      console.log('📊 FETCHING SUPPLIER QUANTITIES');
      console.log('📊 ═══════════════════════════════════════════════════════════');
      console.log('📋 Parameters:');
      console.log('   ├─ Invoice ID:', finalInvoiceId);
      console.log('   ├─ Location ID:', finalLocationId);
      console.log('📊 ═══════════════════════════════════════════════════════════\n');

      const apiUrl = `${baseUrl}/api/GetSupplierQuantities?supplierInvoiceId=${finalInvoiceId}&locationId=${finalLocationId}`;
      
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });

      console.log('\n📥 ═══════════════════════════════════════════════════════════');
      console.log('📥 SUPPLIER QUANTITIES API RESPONSE');
      console.log('📥 ═══════════════════════════════════════════════════════════');
      console.log('📊 Response Status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Error Response Body:', errorText);
        console.log('📥 ═══════════════════════════════════════════════════════════\n');
        throw new Error(`Failed to fetch supplier quantities: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Success Response Data:', JSON.stringify(data, null, 2));
      console.log('📥 ═══════════════════════════════════════════════════════════\n');
      
      // Transform API data to product format using the new JSON structure
      if (data.data && Array.isArray(data.data)) {
        const existingProducts: Product[] = data.data.map((item: any, index: number) => {
          return {
            id: `existing-${finalInvoiceId}-${index}`,
            productName: item.ProductName || '',
            barcode: item.BarCode || '',
            code: item.ProductCode || 0,
            hsnCode: item.HSNCode || '',
            unitCost: item.unitcost || 0,
            quantity: item.quantity || 0,
            discountPer: item.discountper || 0,
            discountAmount: item.discount || 0,
            sgstPer: item.sgstper || 0,
            sgstAmount: item.Sgst || 0,
            cgstPer: item.cgstper || 0,
            cgstAmount: item.Cgst || 0,
            igstPer: item.igstper || 0,
            igstAmount: item.Igst || 0,
            cessPer: item.cessper || 0,
            cessAmount: item.Cess || 0,
            totalCost: item.totalcost || 0,
            availableQuantity: item.Available || 0,
            invoiceQuantity: item.quantity || 0,
            partNo: item.PSerialNumber || item.SerialDetails?.SerialNumber || '',
            hasSerial: item.HasSerial || false,
            serialNumbers: item.SerialDetails ? [{
              id: item.SerialDetails.SerialNumber,
              partNo: item.SerialDetails.SerialNumber,
              spIncGST: item.SerialDetails.Rate || 0,
              batchNo: item.SerialDetails.BatchNo || '',
              mfgDate: item.SerialDetails.MfgDate || '',
              expiryDays: item.SerialDetails.ExpiryDays?.toString() || '',
              auto: false,
              forever: false,
            }] : [],
            // Additional fields from API
            soldQty: item.Sold || 0,
            discount: item.discount || 0,
            sgst: item.Sgst || 0,
            cgst: item.Cgst || 0,
            cess: item.Cess || 0,
            igst: item.Igst || 0,
            returnQuantity: item.returnquantity || 0,
            serialDetails: item.SerialDetails ? [item.SerialDetails] : [],
          };
        });
        
        setProducts(existingProducts);
        console.log(`📦 Loaded ${existingProducts.length} existing products from supplier quantities`);
      }
    } catch (error) {
      console.error('\n💥 ═══════════════════════════════════════════════════════════');
      console.log('💥 FETCH SUPPLIER QUANTITIES ERROR');
      console.log('💥 ═══════════════════════════════════════════════════════════');
      console.error('💥 Error Details:', error);
      console.log('💥 ═══════════════════════════════════════════════════════════\n');
      
      toast({
        variant: 'destructive',
        title: 'Failed to Load Data',
        description: 'Could not load existing supplier quantities. Please try again.',
      });
    }
  };

  // Load existing products if invoiceId is provided
  useEffect(() => {
    if (finalInvoiceId && finalLocationId) {
      fetchSupplierQuantities();
    }
  }, [finalInvoiceId, finalLocationId, baseUrl, authToken]);

  // Calculate totals
  const totals = products.reduce(
    (acc, product) => ({
      quantity: acc.quantity + product.quantity,
      discount: acc.discount + product.discountAmount,
      sgst: acc.sgst + product.sgstAmount,
      cgst: acc.cgst + product.cgstAmount,
      igst: acc.igst + product.igstAmount,
      cess: acc.cess + product.cessAmount,
      total: acc.total + product.totalCost,
    }),
    { quantity: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, cess: 0, total: 0 }
  );

  // Calculate product values
  const calculateProductValues = (product: Product): Product => {
    const unitCost = product.unitCost || 0;
    const quantity = product.quantity || 0;
    const discountPer = product.discountPer || 0;
    const sgstPer = product.sgstPer || 0;
    const cgstPer = product.cgstPer || 0;
    const igstPer = product.igstPer || 0;
    const cessPer = product.cessPer || 0;

    const subtotal = unitCost * quantity;
    const discountAmount = (subtotal * discountPer) / 100;
    const afterDiscount = subtotal - discountAmount;

    const sgstAmount = (afterDiscount * sgstPer) / 100;
    const cgstAmount = (afterDiscount * cgstPer) / 100;
    const igstAmount = (afterDiscount * igstPer) / 100;
    const cessAmount = (afterDiscount * cessPer) / 100;

    const totalCost = afterDiscount + sgstAmount + cgstAmount + igstAmount + cessAmount;

    return {
      ...product,
      discountAmount: Number(discountAmount.toFixed(2)),
      sgstAmount: Number(sgstAmount.toFixed(2)),
      cgstAmount: Number(cgstAmount.toFixed(2)),
      igstAmount: Number(igstAmount.toFixed(2)),
      cessAmount: Number(cessAmount.toFixed(2)),
      totalCost: Number(totalCost.toFixed(2)),
    };
  };

  // Handle product selection from product name dropdown
  const handleNameProductSelect = (product: any) => {
    // Check if product has serial numbers
    if (product.hasserial === 1 || product.hasSerial === 1) {
      console.log('\n🔢 ═══════════════════════════════════════════════════════════');
      console.log('🔢 PRODUCT HAS SERIAL NUMBERS - SHOWING MODAL');
      console.log('🔢 ═══════════════════════════════════════════════════════════');
      console.log('📋 Product Details:');
      console.log('   ├─ Product Name:', product.Title);
      console.log('   ├─ Barcode:', product.Barcode);
      console.log('   ├─ Code:', product.Code);
      console.log('   ├─ Has Serial:', product.hasserial || product.hasSerial);
      console.log('🔢 ═══════════════════════════════════════════════════════════\n');
      
      setCurrentProductForSerial(product);
      setIsSerialModalOpen(true);
      return;
    }

    const sgstPer = isSameState ? parsePercent(product.SgstPer, 0) : 0;
    const cgstPer = isSameState ? parsePercent(product.CGstPer, 0) : 0;
    const igstPer = isSameState ? 0 : parsePercent(product.IgstPer, 0);
    const newProduct: Product = {
      id: `product-${Date.now()}-${Math.random()}`,
      productName: product.Title || '',
      barcode: product.Barcode || '',
      code: product.Code || 0,
      hsnCode: product.HSN || '',
      unitCost: parseFloat(product.CostPrice || '0') || 0,
      quantity: 1,
      discountPer: parsePercent(product.DiscountPer, 0),
      discountAmount: 0,
      sgstPer,
      sgstAmount: 0,
      cgstPer,
      cgstAmount: 0,
      igstPer,
      igstAmount: 0,
      cessPer: parsePercent(product.CessPer, 0),
      cessAmount: 0,
      totalCost: 0,
      availableQuantity: parseFloat(product.Available || '0') || 0,
      invoiceQuantity: 0,
      partNo: product.PartNo || '',
      indentNo: indentNo,
      hasSerial: false,
      serialNumbers: [],
    };

    const calculatedProduct = calculateProductValues(newProduct);
    setProducts([...products, calculatedProduct]);
    setProductNameSearchTerm('');
    setBarcodeSearchTerm('');
    setShowProductNameDropdown(false);
    setShowBarcodeDropdown(false);
  };

  // Handle product selection from barcode dropdown
  const handleBarcodeProductSelect = (product: any) => {
    // Check if product has serial numbers
    if (product.hasserial === 1 || product.hasSerial === 1) {
      console.log('\n🔢 ═══════════════════════════════════════════════════════════');
      console.log('🔢 PRODUCT HAS SERIAL NUMBERS - SHOWING MODAL (BARCODE)');
      console.log('🔢 ═══════════════════════════════════════════════════════════');
      console.log('📋 Product Details:');
      console.log('   ├─ Product Name:', product.Title);
      console.log('   ├─ Barcode:', product.Barcode);
      console.log('   ├─ Code:', product.Code);
      console.log('   ├─ Has Serial:', product.hasserial || product.hasSerial);
      console.log('🔢 ═══════════════════════════════════════════════════════════\n');
      
      setCurrentProductForSerial(product);
      setIsSerialModalOpen(true);
      return;
    }

    const sgstPer = isSameState ? parsePercent(product.SgstPer, 0) : 0;
    const cgstPer = isSameState ? parsePercent(product.CGstPer, 0) : 0;
    const igstPer = isSameState ? 0 : parsePercent(product.IgstPer, 0);
    const newProduct: Product = {
      id: `product-${Date.now()}-${Math.random()}`,
      productName: product.Title || '',
      barcode: product.Barcode || '',
      code: product.Code || 0,
      hsnCode: product.HSN || '',
      unitCost: parseFloat(product.CostPrice || '0') || 0,
      quantity: 1,
      discountPer: parsePercent(product.DiscountPer, 0),
      discountAmount: 0,
      sgstPer,
      sgstAmount: 0,
      cgstPer,
      cgstAmount: 0,
      igstPer,
      igstAmount: 0,
      cessPer: parsePercent(product.CessPer, 0),
      cessAmount: 0,
      totalCost: 0,
      availableQuantity: parseFloat(product.Available || '0') || 0,
      invoiceQuantity: 0,
      partNo: product.PartNo || '',
      indentNo: indentNo,
      hasSerial: false,
      serialNumbers: [],
    };

    const calculatedProduct = calculateProductValues(newProduct);
    setProducts([...products, calculatedProduct]);
    setProductNameSearchTerm('');
    setBarcodeSearchTerm('');
    setShowProductNameDropdown(false);
    setShowBarcodeDropdown(false);
  };

  // Handle adding serial numbers from modal
  const handleSerialNumbersAdded = (serialNumbers: SerialNumber[]) => {
    if (!currentProductForSerial) return;

    console.log('\n✅ ═══════════════════════════════════════════════════════════');
    console.log('✅ SERIAL NUMBERS ADDED FROM MODAL');
    console.log('✅ ═══════════════════════════════════════════════════════════');
    console.log('📋 Product:', currentProductForSerial.Title);
    console.log('📊 Serial Numbers Count:', serialNumbers.length);
    console.log('📝 Serial Numbers:', serialNumbers.map(sn => sn.partNo));
    console.log('💰 Rate Values from Modal:');
    serialNumbers.forEach((sn, index) => {
      console.log(`   ├─ Serial ${index + 1}:`, sn.partNo, 'Rate:', sn.spIncGST);
    });
    console.log('✅ ═══════════════════════════════════════════════════════════\n');

    const p = currentProductForSerial;
    const sgstPer = isSameState ? parsePercent(p.SgstPer, 0) : 0;
    const cgstPer = isSameState ? parsePercent(p.CGstPer, 0) : 0;
    const igstPer = isSameState ? 0 : parsePercent(p.IgstPer, 0);

    // Create separate product rows for each serial number
    const newProducts: Product[] = serialNumbers.map((serialNumber) => {
      const newProduct: Product = {
        id: `product-${Date.now()}-${Math.random()}-${serialNumber.id}`,
        productName: p.Title || '',
        barcode: p.Barcode || '',
        code: p.Code || 0,
        hsnCode: p.HSN || '',
        unitCost: parseFloat(p.CostPrice || '0') || 0,
        quantity: 1, // Each serial number has quantity 1
        discountPer: parsePercent(p.DiscountPer, 0),
        discountAmount: 0,
        sgstPer,
        sgstAmount: 0,
        cgstPer,
        cgstAmount: 0,
        igstPer,
        igstAmount: 0,
        cessPer: parsePercent(p.CessPer, 0),
        cessAmount: 0,
        totalCost: 0,
        availableQuantity: parseFloat(p.Available || '0') || 0,
        invoiceQuantity: 0,
        partNo: p.PartNo || '',
        indentNo: indentNo,
        hasSerial: true,
        serialNumbers: [serialNumber], // Each row has only one serial number
      };

      return calculateProductValues(newProduct);
    });

    // Add all new products to the products array
    setProducts([...products, ...newProducts]);
    setProductNameSearchTerm('');
    setBarcodeSearchTerm('');
    setShowProductNameDropdown(false);
    setShowBarcodeDropdown(false);
    setCurrentProductForSerial(null);
  };

  // Auto-select when barcode matches exactly one product
  useEffect(() => {
    if (barcodeProducts.length === 1 && debouncedBarcodeSearch.trim() && barcodeProducts[0].Barcode === debouncedBarcodeSearch.trim()) {
      handleBarcodeProductSelect(barcodeProducts[0]);
      setBarcodeSearchTerm('');
    }
  }, [barcodeProducts, debouncedBarcodeSearch]);

  // Handle field changes in table
  const handleFieldChange = (id: string, field: keyof Product, value: string | number) => {
    const rawKey = `${field}-${id}`;
    setRawInputValues((prev) => ({ ...prev, [rawKey]: String(value) }));

    // Check if this is a quantity change for a product with serial numbers
    if (field === 'quantity') {
      const product = products.find(p => p.id === id);
      if (product && product.hasSerial && product.serialNumbers && product.serialNumbers.length > 0) {
        const newQuantity = parseFloat(String(value)) || 0;
        if (newQuantity !== 1) { // Each serial number row must have quantity 1
          toast({
            variant: 'destructive',
            title: 'Quantity Restriction',
            description: 'Quantity must be 1 for products with serial numbers',
          });
          console.log('\n⚠️ ═══════════════════════════════════════════════════════════');
          console.log('⚠️ QUANTITY RESTRICTION ERROR');
          console.log('⚠️ ═══════════════════════════════════════════════════════════');
          console.log('📋 Product:', product.productName);
          console.log('📊 Attempted Quantity:', newQuantity);
          console.log('📊 Required Quantity: 1 (single serial per row)');
          console.log('⚠️ ═══════════════════════════════════════════════════════════\n');
          return; // Don't update the quantity
        }
      }
    }

    setProducts((prev) => {
      const updated = prev.map((p) => {
        if (p.id === id) {
          const updatedProduct = { ...p, [field]: value };
          return calculateProductValues(updatedProduct);
        }
        return p;
      });
      return updated;
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    productId: string,
    field: string
  ) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      // Focus next editable field
      const editableFields = ['unitCost', 'quantity', 'discountPer', 'sgstPer', 'cgstPer', 'igstPer', 'cessPer', 'totalCost'];
      const currentIndex = editableFields.indexOf(field);
      if (currentIndex < editableFields.length - 1) {
        const nextField = editableFields[currentIndex + 1];
        const nextInput = document.querySelector(
          `input[data-product-id="${productId}"][data-field="${nextField}"]`
        ) as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    }
  };

  // Handle product deletion
  const handleDeleteSelected = () => {
    if (selectedProducts.size === 0) {
      toast({
        variant: 'destructive',
        title: 'No Selection',
        description: 'Please select at least one product to delete.',
      });
      return;
    }

    setProducts((prev) => prev.filter((p) => !selectedProducts.has(p.id)));
    setSelectedProducts(new Set());
    toast({
      title: 'Products Deleted',
      description: `${selectedProducts.size} product(s) removed.`,
    });
  };

  // Handle Excel import
  const handleExcelImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const importedProducts: Product[] = jsonData.map((row: any, index) => {
        const product: Product = {
          id: `imported-${Date.now()}-${index}`,
          productName: row['Product Name'] || row['ProductName'] || row['productName'] || '',
          barcode: row['Barcode'] || row['barcode'] || '',
          code: parseFloat(row['Code'] || row['code'] || '0') || 0,
          hsnCode: row['HSN Code'] || row['HSNCode'] || row['hsnCode'] || '',
          unitCost: parseFloat(row['Unit Cost'] || row['UnitCost'] || row['unitCost'] || '0') || 0,
          quantity: parseFloat(row['Quantity'] || row['quantity'] || '1') || 1,
          discountPer: parseFloat(row['Discount %'] || row['Discount%'] || row['discountPer'] || '0') || 0,
          discountAmount: 0,
          sgstPer: parseFloat(row['SGST %'] || row['SGST%'] || row['sgstPer'] || '0') || 0,
          sgstAmount: 0,
          cgstPer: parseFloat(row['CGST %'] || row['CGST%'] || row['cgstPer'] || '0') || 0,
          cgstAmount: 0,
          igstPer: parseFloat(row['IGST %'] || row['IGST%'] || row['igstPer'] || '0') || 0,
          igstAmount: 0,
          cessPer: parseFloat(row['Cess %'] || row['Cess%'] || row['cessPer'] || '0') || 0,
          cessAmount: 0,
          totalCost: 0,
          availableQuantity: parseFloat(row['Available Quantity'] || row['AvailableQuantity'] || row['availableQuantity'] || '0') || 0,
          invoiceQuantity: parseFloat(row['Invoice Quantity'] || row['InvoiceQuantity'] || row['invoiceQuantity'] || '0') || 0,
          partNo: row['Part No'] || row['PartNo'] || row['partNo'] || '',
        };

        return calculateProductValues(product);
      });

      setProducts((prev) => [...prev, ...importedProducts]);
      toast({
        title: 'Import Successful',
        description: `${importedProducts.length} product(s) imported from Excel.`,
      });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Excel import error:', error);
      toast({
        variant: 'destructive',
        title: 'Import Failed',
        description: 'Failed to import Excel file. Please check the file format.',
      });
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!finalInvoiceId || !finalSupplierId) {
      toast({
        variant: 'destructive',
        title: 'Missing Information',
        description: 'Invoice ID or Supplier ID is missing.',
      });
      return;
    }

    try {
      setIsSaving(true);
      console.log('\n💾 ═══════════════════════════════════════════════════════════');
      console.log('💾 SAVING SUPPLIER QUANTITIES');
      console.log('💾 ═══════════════════════════════════════════════════════════');
      console.log('📋 Save Parameters:');
      console.log('   ├─ Invoice ID:', finalInvoiceId);
      console.log('   ├─ Supplier ID:', finalSupplierId);
      console.log('   ├─ Products Count:', products.length);
      console.log('💾 ═══════════════════════════════════════════════════════════\n');

      // Build QuantityDetails array
      let QuantityDetails: any[] = [];
      
      if (products.length > 0) {
        QuantityDetails = products.map(product => {
          const baseQuantityDetail: any = {
            ProductCode: String(product.code || 0),
            HasSerial: product.hasSerial || false,
            Sgst: product.sgstAmount || 0,
            Cgst: product.cgstAmount || 0,
            Cess: product.cessAmount || 0,
            Igst: product.igstAmount || 0,
            sgstper: product.sgstPer || 0,
            cgstper: product.cgstPer || 0,
            cessper: product.cessPer || 0,
            igstper: product.igstPer || 0,
            unitcost: product.unitCost || 0,
            totalcost: product.totalCost || 0,
            discount: product.discountAmount || 0,
            quantity: product.quantity || 0,
            returnquantity: 0,
            discountper: product.discountPer || 0,
            SerialDetails: product.hasSerial && product.serialNumbers && product.serialNumbers.length > 0
              ? {
                  SerialNumber: product.serialNumbers[0].partNo || '',
                  Quantity: product.serialNumbers[0].auto ? 1 : 1, // Default to 1 since quantity doesn't exist in SerialNumber
                  Rate: parseFloat(product.serialNumbers[0].spIncGST?.toString() || '0') || 0,
                  CostPrice: String(product.serialNumbers[0].costPrice ?? 0),
                  MfgDate: product.serialNumbers[0].mfgDate || new Date().toISOString().split('T')[0],
                  ExpiryDays: (() => {
                    const p = product.serialNumbers[0];
                    if (p.forever) return 999999;
                    const parsed = parseInt(p.expiryDays, 10);
                    return isNaN(parsed) ? 0 : parsed;
                  })(),
                  BatchNo: product.serialNumbers[0].batchNo || ''
                }
              : {}
          };

          // Debug logging for serial details
          if (product.hasSerial && product.serialNumbers && product.serialNumbers.length > 0) {
            console.log(`🔍 DEBUG - Serial Details for ${product.productName}:`);
            console.log('   ├─ Part No:', product.serialNumbers[0].partNo);
            console.log('   ├─ spIncGST (raw):', product.serialNumbers[0].spIncGST);
            console.log('   ├─ spIncGST (type):', typeof product.serialNumbers[0].spIncGST);
            console.log('   ├─ Rate being sent:', parseFloat(product.serialNumbers[0].spIncGST?.toString() || '0') || 0);
            console.log('   └─ ExpiryDays being sent:', (() => {
              const p = product.serialNumbers[0];
              if (p.forever) return 999999;
              const parsed = parseInt(p.expiryDays, 10);
              return isNaN(parsed) ? 0 : parsed;
            })());
          }

          return baseQuantityDetail;
        });

        // Add the overwrite options object as a separate item
        const overwriteOptions = {
          Overwrite_Existing: isDebitNote ? false : overwriteOption === 'overwrite',
          Average_with_Previous: isDebitNote ? false : overwriteOption === 'average',
          Maximum_Of_Costprice: isDebitNote ? false : overwriteOption === 'maximum'
        };
        QuantityDetails.push(overwriteOptions as any);
      }

      // Build the final payload
      const payload = {
        Quantities: {
          SupplierInvoiceId: finalInvoiceId,
          SupplierID: finalSupplierId,
          QuantityDetails: QuantityDetails
        }
      };

      console.log('\n📤 ═══════════════════════════════════════════════════════════');
      console.log('📤 API PAYLOAD');
      console.log('📤 ═══════════════════════════════════════════════════════════');
      const apiUrl = isDebitNote ? `${baseUrl}/api/SupplierQuantitiesDebit` : `${baseUrl}/api/SupplierQuantities`;
      console.log('🌐 API URL:', apiUrl);
      console.log('📦 Payload:', JSON.stringify(payload, null, 2));
      console.log('📤 ═══════════════════════════════════════════════════════════\n');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('\n📥 ═══════════════════════════════════════════════════════════');
      console.log('📥 SUPPLIER QUANTITIES API RESPONSE');
      console.log('📥 ═══════════════════════════════════════════════════════════');
      console.log('📊 Response Status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Error Response Body:', errorText);
        console.log('📥 ═══════════════════════════════════════════════════════════\n');
        throw new Error(`Failed to save supplier quantities: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('✅ Success Response Data:', JSON.stringify(responseData, null, 2));
      console.log('📥 ═══════════════════════════════════════════════════════════\n');

      // When product grid is empty and response contains deleted serial(s) (debit only): show Create Serial popup
      const hasDeletedSerialMessage = isDebitNote && responseData.message && /Deleted \d+ serial number\(s\)?: .+ \(Product: \d+\)/.test(responseData.message);
      const isGridEmpty = products.length === 0 || responseData?.TotalRecords === 0;
      if (hasDeletedSerialMessage && !isGridEmpty) {
        console.log('ℹ️ Deleted serial message detected, but grid not empty. Popup not shown.', {
          productsLength: products.length,
          totalRecords: responseData?.TotalRecords,
          message: responseData?.message,
        });
      }
      if (hasDeletedSerialMessage && isGridEmpty) {
        // Parse all "SerialNumber (Product: id)" from message (supports single and multiple)
        const regex = /(\S+)\s+\(Product:\s*(\d+)\)/g;
        const parsed: Array<{ serialNumber: string; productId: number }> = [];
        let m;
        while ((m = regex.exec(responseData.message)) !== null) {
          parsed.push({ serialNumber: (m[1] || '').trim(), productId: parseInt(m[2], 10) });
        }
        if (parsed.length > 0) {
          const locationIdVal = finalLocationId || 0;
          const serials = parsed.map(({ serialNumber, productId }) => ({
            productId,
            serialNumber,
            locationId: locationIdVal,
          }));
          console.log('\n🔍 DELETED SERIAL(S) DETECTED:', serials.length, serials);

          if (serials.length === 1) {
            setDeletedSerialInfo(serials[0]);
            setDeletedSerials([]);
            setSerialFormData({
              batchNo: '0',
              mrp: '0',
              rate: '0',
              tag1: '',
              tag2: '',
              mfgDate: new Date().toISOString().split('T')[0],
              expiryDays: expiryForeverDefault ? '999999' : '365',
              forever: expiryForeverDefault,
            });
          } else {
            setDeletedSerialInfo(null);
            setDeletedSerials(serials);
            const today = new Date().toISOString().split('T')[0];
            setSerialTableFormData(serials.map(() => ({
              mfgDate: today,
              batchNo: '0',
              rate: '0',
              costPrice: '0',
              forever: expiryForeverDefault,
              expiryDays: expiryForeverDefault ? '999999' : '0',
            })));
          }
          setShowSerialPopup(true);
          toast({
            title: 'Deleted Serial Number(s)',
            description: serials.length === 1
              ? `Serial number ${serials[0].serialNumber} was deleted. Please recreate it.`
              : `${serials.length} serial numbers were deleted. Please recreate them.`,
          });
          return;
        }
      }

      // Show message based on API response status (do not show API success message when status = 0)
      if (responseData.status === -1) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: responseData.message || 'An error occurred.',
        });
      } else if (responseData.status === 0) {
        toast({
          title: 'Saved Successfully',
          description: 'Supplier Quantities Processed Successfully',
        });
        // Navigate back after save (only when status = 0 and no popup shown)
        setTimeout(() => {
          handleBack();
        }, 1000);
      }
    } catch (error) {
      console.error('\n💥 ═══════════════════════════════════════════════════════════');
      console.log('💥 SAVE ERROR');
      console.log('💥 ═══════════════════════════════════════════════════════════');
      console.error('💥 Error Details:', error);
      console.log('💥 ═══════════════════════════════════════════════════════════\n');
      
      toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: 'Failed to save product quantities. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle CreateSerial API call from popup
  const handleCreateSerial = async () => {
    if (!deletedSerialInfo) {
      toast({
        variant: 'destructive',
        title: 'Missing Information',
        description: 'Serial information is missing.',
      });
      return;
    }

    try {
      console.log('\n🔧 ═══════════════════════════════════════════════════════════');
      console.log('🔧 CREATING SERIAL NUMBER');
      console.log('🔧 ═══════════════════════════════════════════════════════════');
      console.log('📋 Serial Details:');
      console.log('   ├─ Product ID:', deletedSerialInfo.productId);
      console.log('   ├─ Serial Number:', deletedSerialInfo.serialNumber);
      console.log('   ├─ Location ID:', deletedSerialInfo.locationId);
      console.log('   ├─ Batch No:', serialFormData.batchNo);
      console.log('   ├─ MRP:', serialFormData.mrp);
      console.log('   ├─ Rate:', serialFormData.rate);
      console.log('   ├─ Mfg Date:', serialFormData.mfgDate);
      console.log('   ├─ Expiry Days:', serialFormData.forever ? 999999 : serialFormData.expiryDays);
      console.log('   └─ Forever:', serialFormData.forever);
      console.log('🔧 ═══════════════════════════════════════════════════════════\n');

      // MfgDate as ISO string (e.g. "2023-12-01T00:00:00"); API accepts array, include suppinvno (debit note row id)
      const mfgDateIso = serialFormData.mfgDate.includes('T') ? serialFormData.mfgDate : `${serialFormData.mfgDate}T00:00:00`;
      const payload = [{
        ProductId: deletedSerialInfo.productId,
        SerialNumber: deletedSerialInfo.serialNumber,
        Rate: parseFloat(serialFormData.rate) || 0,
        MfgDate: mfgDateIso,
        ExpiryDays: serialFormData.forever ? 999999 : (parseInt(serialFormData.expiryDays, 10) || 0),
        LocationId: deletedSerialInfo.locationId,
        BatchNo: serialFormData.batchNo || '0',
        suppinvno: String(finalInvoiceId ?? ''),
        CostPrice: parseFloat(serialFormData.mrp) || 0,
      }];

      console.log('\n🌐 ═══════════════════════════════════════════════════════════');
      console.log('🌐 CREATE SERIAL API REQUEST');
      console.log('🌐 ═══════════════════════════════════════════════════════════');
      console.log('🌐 API URL:', `${baseUrl}/api/CreateSerial`);
      console.log('📦 Request Payload:', JSON.stringify(payload, null, 2));
      console.log('🌐 ═══════════════════════════════════════════════════════════\n');

      const response = await fetch(`${baseUrl}/api/CreateSerial`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('\n📥 ═══════════════════════════════════════════════════════════');
      console.log('📥 CREATE SERIAL API RESPONSE');
      console.log('📥 ═══════════════════════════════════════════════════════════');
      console.log('📊 Response Status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Error Response Body:', errorText);
        console.log('📥 ═══════════════════════════════════════════════════════════\n');
        throw new Error(`Failed to create serial: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('✅ Response JSON:', JSON.stringify(responseData, null, 2));
      console.log('📥 ═══════════════════════════════════════════════════════════\n');

      toast({
        title: 'Serial Created Successfully',
        description: `Serial number ${deletedSerialInfo.serialNumber} has been recreated.`,
      });

      setShowSerialPopup(false);
      setDeletedSerialInfo(null);
      setDeletedSerials([]);
      setSerialFormData({
        batchNo: '0',
        mrp: '0',
        rate: '0',
        tag1: '',
        tag2: '',
        mfgDate: new Date().toISOString().split('T')[0],
        expiryDays: expiryForeverDefault ? '999999' : '365',
        forever: expiryForeverDefault,
      });

      // Close SupplierInvoiceProductEntry and go to Invoice & Payments
      setTimeout(() => {
        if (fromInvoicesPayments && backToSupplierIds.length > 0) {
          queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
          queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
          navigate('/supplier-invoices-payments', {
            state: {
              supplierIds: backToSupplierIds,
              supplierState: backToSupplierState,
            },
            replace: true,
          });
        } else {
          handleBack();
        }
      }, 1000);

    } catch (error) {
      console.error('\n💥 ═══════════════════════════════════════════════════════════');
      console.log('💥 CREATE SERIAL ERROR');
      console.log('💥 ═══════════════════════════════════════════════════════════');
      console.error('💥 Error Details:', error);
      console.log('💥 ═══════════════════════════════════════════════════════════\n');
      
      toast({
        variant: 'destructive',
        title: 'Failed to Create Serial',
        description: 'Failed to recreate serial number. Please try again.',
      });
    }
  };

  // Handle CreateSerial for multiple deleted serials (table submit)
  const handleCreateSerialMultiple = async () => {
    if (!deletedSerials.length || deletedSerials.length !== serialTableFormData.length) {
      toast({
        variant: 'destructive',
        title: 'Missing Information',
        description: 'Serial or form data is missing.',
      });
      return;
    }
    // Validate: mandatory MFG Date and Rate for every row
    const invalidRow = serialTableFormData.findIndex(
      (row) => !row.mfgDate?.trim() || (parseFloat(row.rate) ?? 0) < 0 || (!row.forever && row.expiryDays.trim() === '')
    );
    if (invalidRow >= 0) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: `Please fill MFG Date and Rate for row ${invalidRow + 1}.`,
      });
      return;
    }
    setIsSubmittingSerials(true);
    try {
      const payload = deletedSerials.map((serial, i) => {
        const row = serialTableFormData[i];
        const mfgDateIso = row.mfgDate.includes('T') ? row.mfgDate : `${row.mfgDate}T00:00:00`;
        const expiryDays = row.forever ? 999999 : (parseInt(row.expiryDays, 10) || 0);
        return {
          ProductId: serial.productId,
          SerialNumber: serial.serialNumber,
          Rate: parseFloat(row.rate) || 0,
          MfgDate: mfgDateIso,
          ExpiryDays: expiryDays,
          LocationId: serial.locationId,
          BatchNo: row.batchNo || '0',
          suppinvno: String(finalInvoiceId ?? ''),
          CostPrice: parseFloat(row.costPrice) || 0,
        };
      });

      console.log('\n🌐 ═══════════════════════════════════════════════════════════');
      console.log('🌐 CREATE SERIAL (MULTIPLE) API REQUEST');
      console.log('🌐 ═══════════════════════════════════════════════════════════');
      console.log('🌐 API URL:', `${baseUrl}/api/CreateSerial`);
      console.log('📦 Request Payload:', JSON.stringify(payload, null, 2));
      console.log('🌐 ═══════════════════════════════════════════════════════════\n');
      const response = await fetch(`${baseUrl}/api/CreateSerial`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to create serials: ${response.status}`);
      }
      const responseData = await response.json();
      console.log('✅ Response JSON:', JSON.stringify(responseData, null, 2));
      toast({
        title: 'Serials Created Successfully',
        description: `${deletedSerials.length} serial number(s) have been recreated.`,
      });
      setShowSerialPopup(false);
      setDeletedSerials([]);
      setSerialTableFormData([]);
      setTimeout(() => {
        if (fromInvoicesPayments && backToSupplierIds.length > 0) {
          queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
          queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
          navigate('/supplier-invoices-payments', {
            state: { supplierIds: backToSupplierIds, supplierState: backToSupplierState },
            replace: true,
          });
        } else {
          handleBack();
        }
      }, 1000);
    } catch (error) {
      console.error('CreateSerial (multiple) error:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to Create Serials',
        description: 'Failed to recreate serial numbers. Please try again.',
      });
    } finally {
      setIsSubmittingSerials(false);
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  // Toggle row expansion (mobile)
  const toggleRowExpansion = (productId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductNameDropdown(false);
      }
      if (barcodeDropdownRef.current && !barcodeDropdownRef.current.contains(event.target as Node)) {
        setShowBarcodeDropdown(false);
      }
    };

    if (showProductNameDropdown || showBarcodeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductNameDropdown, showBarcodeDropdown]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          title="Supplier Quantities"
          onBack={handleBack}
          showBackButton={true}
        />
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <div className="sticky top-0 z-10 bg-white border-b px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Supplier Quantities to be Added</h1>
              <p className="text-sm text-gray-600 mt-1">
                {finalSupplierName} (Invoice No: {finalInvoiceRefNo})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <div className="px-4 pt-16 pb-4 bg-white border-b">
          <h1 className="text-lg font-bold text-gray-900">Supplier Quantities to be Added</h1>
          <p className="text-sm text-gray-600 mt-1">
            {finalSupplierName} (Invoice No: {finalInvoiceRefNo})
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Product Entry Form */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Add Product</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn(
              'grid gap-4',
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            )}>
              {/* Product Name */}
              <div className="space-y-2 relative">
                <div className="flex items-center justify-between">
                  <Label htmlFor="productName">Product Name</Label>
                  <button
                    type="button"
                    onClick={() => setShowAddProductModal(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Create New Product
                  </button>
                </div>
                <div className="relative" ref={productDropdownRef}>
                  <Input
                    id="productName"
                    placeholder="Search product name..."
                    value={productNameSearchTerm}
                    onChange={(e) => {
                      setProductNameSearchTerm(e.target.value);
                      setShowProductNameDropdown(true);
                    }}
                    onFocus={() => {
                      if (productNameSearchTerm) {
                        setShowProductNameDropdown(true);
                      }
                    }}
                    onKeyDown={(e) => {
                      const len = productNameProducts?.length || 0;
                      if (!showProductNameDropdown || len === 0) return;
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setProductNameDropdownActiveIndex((prev) => {
                          const next = prev + 1;
                          return next >= len ? 0 : next;
                        });
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setProductNameDropdownActiveIndex((prev) => {
                          const next = prev - 1;
                          return next < 0 ? len - 1 : next;
                        });
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        const idx = productNameDropdownActiveIndex >= 0 ? productNameDropdownActiveIndex : 0;
                        const product = productNameProducts[idx];
                        if (product) {
                          handleNameProductSelect(product);
                        }
                      }
                    }}
                  />
                  {showProductNameDropdown && productNameSearchTerm.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {productNameSearchTerm !== debouncedProductNameSearch ? (
                        <div className="p-2 text-xs text-gray-500">Typing...</div>
                      ) : isProductNameLoading ? (
                        <div className="p-2 text-xs text-gray-500">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                        </div>
                      ) : productNameProducts.length > 0 ? (
                        productNameProducts.map((product: any, index: number) => {
                          return (
                            <div
                              key={`name-${product.Barcode}`}
                              className={`product-name-dropdown-item p-2 text-xs border-b last:border-b-0 transition flex items-center gap-2 cursor-pointer ${index === productNameDropdownActiveIndex ? 'bg-blue-100' : 'hover:bg-blue-100'}`}
                              onClick={() => {
                                handleNameProductSelect(product);
                              }}
                            >
                              <img
                                src={product.imgstr || '/placeholder.svg'}
                                alt={product.Title}
                                className="w-8 h-8 object-contain rounded border"
                                onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder.svg'; }}
                              />
                              <div className="flex-1">
                                <div className="font-medium">{product.Title}</div>
                                <div className="text-gray-600">{product.Barcode}</div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-2 text-xs text-gray-500">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Barcode */}
              <div className="space-y-2 relative">
                <Label htmlFor="barcode">Product Barcode</Label>
                <div className="relative" ref={barcodeDropdownRef}>
                  <Input
                    id="barcode"
                    placeholder="Scan or enter barcode"
                    value={barcodeSearchTerm}
                    onChange={(e) => {
                      setBarcodeSearchTerm(e.target.value);
                      setShowBarcodeDropdown(true);
                    }}
                    onFocus={() => {
                      if (barcodeSearchTerm) {
                        setShowBarcodeDropdown(true);
                      }
                    }}
                  />
                  {showBarcodeDropdown && barcodeSearchTerm.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {barcodeSearchTerm !== debouncedBarcodeSearch ? (
                        <div className="p-2 text-xs text-gray-500">Typing...</div>
                      ) : isBarcodeLoading ? (
                        <div className="p-2 text-xs text-gray-500">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                        </div>
                      ) : barcodeProducts.length > 0 ? (
                        barcodeProducts.map((product: any) => {
                          return (
                            <div
                              key={`barcode-${product.Barcode}`}
                              className="p-2 text-xs border-b last:border-b-0 transition flex items-center gap-2 hover:bg-blue-100 cursor-pointer"
                              onClick={() => {
                                handleBarcodeProductSelect(product);
                              }}
                            >
                              <img
                                src={product.imgstr || '/placeholder.svg'}
                                alt={product.Title}
                                className="w-8 h-8 object-contain rounded border"
                                onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder.svg'; }}
                              />
                              <div className="flex-1">
                                <div className="font-medium">{product.Barcode}</div>
                                <div className="text-gray-600">{product.Title}</div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-2 text-xs text-gray-500">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Indent No */}
              <div className="space-y-2">
                <Label htmlFor="indentNo">Indent No</Label>
                <Input
                  id="indentNo"
                  placeholder="Enter indent number"
                  value={indentNo}
                  onChange={(e) => setIndentNo(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant="outline"
            onClick={handleExcelImport}
            className="flex items-center gap-2 hidden"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excel Import
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            variant="destructive"
            onClick={handleDeleteSelected}
            disabled={selectedProducts.size === 0}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected Row
          </Button>
        </div>

        {/* Products Table - Desktop */}
        {!isMobile && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-50 z-10">
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={products.length > 0 && selectedProducts.size === products.length}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedProducts(new Set(products.map((p) => p.id)));
                            } else {
                              setSelectedProducts(new Set());
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Barcode</TableHead>
                      <TableHead>HSN Code</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Discount %</TableHead>
                      <TableHead className="text-right">Discount Amount</TableHead>
                      <TableHead className="text-right">SGST %</TableHead>
                      <TableHead className="text-right">SGST Amount</TableHead>
                      <TableHead className="text-right">CGST %</TableHead>
                      <TableHead className="text-right">CGST Amount</TableHead>
                      <TableHead className="text-right">IGST %</TableHead>
                      <TableHead className="text-right">IGST Amount</TableHead>
                      <TableHead className="text-right">Cess %</TableHead>
                      <TableHead className="text-right">Cess Amount</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      <TableHead className="text-right">Available Qty</TableHead>
                      <TableHead>Part No</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={18} className="text-center py-8 text-gray-500">
                          No products added. Use the form above to add products.
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedProducts.has(product.id)}
                              onCheckedChange={(checked) => {
                                const newSet = new Set(selectedProducts);
                                if (checked) {
                                  newSet.add(product.id);
                                } else {
                                  newSet.delete(product.id);
                                }
                                setSelectedProducts(newSet);
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{product.productName}</TableCell>
                          <TableCell>{product.barcode}</TableCell>
                          <TableCell>{product.hsnCode}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-24 text-right no-spinner"
                              value={rawInputValues[`unitCost-${product.id}`] ?? product.unitCost}
                              onChange={(e) => handleFieldChange(product.id, 'unitCost', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'unitCost')}
                              data-product-id={product.id}
                              data-field="unitCost"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="1"
                              className="w-20 text-right no-spinner"
                              value={rawInputValues[`quantity-${product.id}`] ?? product.quantity}
                              onChange={(e) => handleFieldChange(product.id, 'quantity', parseInt(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'quantity')}
                              data-product-id={product.id}
                              data-field="quantity"
                              disabled={product.hasSerial}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-20 text-right no-spinner"
                              value={rawInputValues[`discountPer-${product.id}`] ?? product.discountPer}
                              onChange={(e) => handleFieldChange(product.id, 'discountPer', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'discountPer')}
                              data-product-id={product.id}
                              data-field="discountPer"
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(product.discountAmount)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-20 text-right no-spinner"
                              value={rawInputValues[`sgstPer-${product.id}`] ?? product.sgstPer}
                              onChange={(e) => handleFieldChange(product.id, 'sgstPer', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'sgstPer')}
                              data-product-id={product.id}
                              data-field="sgstPer"
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(product.sgstAmount)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-20 text-right no-spinner"
                              value={rawInputValues[`cgstPer-${product.id}`] ?? product.cgstPer}
                              onChange={(e) => handleFieldChange(product.id, 'cgstPer', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'cgstPer')}
                              data-product-id={product.id}
                              data-field="cgstPer"
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(product.cgstAmount)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-20 text-right no-spinner"
                              value={rawInputValues[`igstPer-${product.id}`] ?? product.igstPer}
                              onChange={(e) => handleFieldChange(product.id, 'igstPer', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'igstPer')}
                              data-product-id={product.id}
                              data-field="igstPer"
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(product.igstAmount)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-20 text-right no-spinner"
                              value={rawInputValues[`cessPer-${product.id}`] ?? product.cessPer}
                              onChange={(e) => handleFieldChange(product.id, 'cessPer', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'cessPer')}
                              data-product-id={product.id}
                              data-field="cessPer"
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(product.cessAmount)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="w-24 text-right no-spinner"
                              value={rawInputValues[`totalCost-${product.id}`] ?? product.totalCost}
                              onChange={(e) => handleFieldChange(product.id, 'totalCost', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, product.id, 'totalCost')}
                              data-product-id={product.id}
                              data-field="totalCost"
                            />
                          </TableCell>
                          <TableCell className="text-right">{product.availableQuantity}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {product.hasSerial && product.serialNumbers && product.serialNumbers.length > 0 ? (
                                <div className="text-xs bg-blue-50 text-blue-700 p-1 rounded border border-blue-200">
                                  {product.serialNumbers[0].partNo}
                                </div>
                              ) : product.serialDetails && product.serialDetails.length > 0 ? (
                                <div className="text-xs space-y-1">
                                  {product.serialDetails.map((serial, index) => (
                                    <div key={index} className="bg-gray-50 p-1 rounded border">
                                      <div className="font-medium">{serial.SerialNumber}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="font-medium">{product.partNo || '-'}</div>
                              )}
                              {!product.hasSerial && (!product.serialDetails || product.serialDetails.length === 0) ? (
                                <span className="text-gray-400 text-xs">No serial numbers</span>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Cards - Mobile */}
        {isMobile && (
          <div className="space-y-4">
            {products.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No products added. Use the form above to add products.
                </CardContent>
              </Card>
            ) : (
              products.map((product) => {
                const isExpanded = expandedRows.has(product.id);
                return (
                  <Card key={product.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="font-semibold text-base mb-1">{product.productName}</div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div>Barcode: {product.barcode}</div>
                            <div>HSN: {product.hsnCode}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={(checked) => {
                              const newSet = new Set(selectedProducts);
                              if (checked) {
                                newSet.add(product.id);
                              } else {
                                newSet.delete(product.id);
                              }
                              setSelectedProducts(newSet);
                            }}
                          />
                          <button
                            onClick={() => toggleRowExpansion(product.id)}
                            className="p-1"
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5" />
                            ) : (
                              <ChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Key Fields - Always Visible */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-gray-500">Quantity</Label>
                          <Input
                            type="number"
                            step="1"
                            className="mt-1 no-spinner"
                            value={rawInputValues[`quantity-${product.id}`] ?? product.quantity}
                            onChange={(e) => handleFieldChange(product.id, 'quantity', parseInt(e.target.value) || 0)}
                            data-product-id={product.id}
                            data-field="quantity"
                            disabled={product.hasSerial}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Unit Cost</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="mt-1 no-spinner"
                            value={rawInputValues[`unitCost-${product.id}`] ?? product.unitCost}
                            onChange={(e) => handleFieldChange(product.id, 'unitCost', parseFloat(e.target.value) || 0)}
                            data-product-id={product.id}
                            data-field="unitCost"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-gray-500">Total Cost</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="mt-1 no-spinner"
                            value={rawInputValues[`totalCost-${product.id}`] ?? product.totalCost}
                            onChange={(e) => handleFieldChange(product.id, 'totalCost', parseFloat(e.target.value) || 0)}
                            data-product-id={product.id}
                            data-field="totalCost"
                          />
                        </div>
                      </div>

                      {/* Expandable Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-gray-500">Discount %</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="mt-1 no-spinner"
                                value={rawInputValues[`discountPer-${product.id}`] ?? product.discountPer}
                                onChange={(e) => handleFieldChange(product.id, 'discountPer', parseFloat(e.target.value) || 0)}
                                data-product-id={product.id}
                                data-field="discountPer"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Discount Amount</Label>
                              <div className="mt-1 text-sm font-medium">{formatCurrency(product.discountAmount)}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">SGST %</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="mt-1 no-spinner"
                                value={rawInputValues[`sgstPer-${product.id}`] ?? product.sgstPer}
                                onChange={(e) => handleFieldChange(product.id, 'sgstPer', parseFloat(e.target.value) || 0)}
                                data-product-id={product.id}
                                data-field="sgstPer"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">SGST Amount</Label>
                              <div className="mt-1 text-sm font-medium">{formatCurrency(product.sgstAmount)}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">CGST %</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="mt-1 no-spinner"
                                value={rawInputValues[`cgstPer-${product.id}`] ?? product.cgstPer}
                                onChange={(e) => handleFieldChange(product.id, 'cgstPer', parseFloat(e.target.value) || 0)}
                                data-product-id={product.id}
                                data-field="cgstPer"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">CGST Amount</Label>
                              <div className="mt-1 text-sm font-medium">{formatCurrency(product.cgstAmount)}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">IGST %</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="mt-1 no-spinner"
                                value={rawInputValues[`igstPer-${product.id}`] ?? product.igstPer}
                                onChange={(e) => handleFieldChange(product.id, 'igstPer', parseFloat(e.target.value) || 0)}
                                data-product-id={product.id}
                                data-field="igstPer"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">IGST Amount</Label>
                              <div className="mt-1 text-sm font-medium">{formatCurrency(product.igstAmount)}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Cess %</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="mt-1 no-spinner"
                                value={rawInputValues[`cessPer-${product.id}`] ?? product.cessPer}
                                onChange={(e) => handleFieldChange(product.id, 'cessPer', parseFloat(e.target.value) || 0)}
                                data-product-id={product.id}
                                data-field="cessPer"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Cess Amount</Label>
                              <div className="mt-1 text-sm font-medium">{formatCurrency(product.cessAmount)}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Available Qty</Label>
                              <div className="mt-1 text-sm">{product.availableQuantity}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Invoice Qty</Label>
                              <div className="mt-1 text-sm">{product.invoiceQuantity}</div>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Part No</Label>
                              <div className="mt-1 text-sm space-y-1">
                                {product.hasSerial && product.serialNumbers && product.serialNumbers.length > 0 ? (
                                  <div className="text-xs bg-blue-50 text-blue-700 p-1 rounded border border-blue-200">
                                    {product.serialNumbers[0].partNo}
                                  </div>
                                ) : product.serialDetails && product.serialDetails.length > 0 ? (
                                  <div className="text-xs space-y-1">
                                    {product.serialDetails.map((serial, index) => (
                                      <div key={index} className="bg-gray-50 p-2 rounded border">
                                        <div className="font-medium">{serial.SerialNumber}</div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="font-medium">{product.partNo || '-'}</div>
                                )}
                                {!product.hasSerial && (!product.serialDetails || product.serialDetails.length === 0) ? (
                                  <span className="text-gray-400 text-xs">No serial numbers</span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Summary Section */}
        {products.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={cn(
                'grid gap-4',
                isMobile ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
              )}>
                <div>
                  <Label className="text-xs text-gray-500">Total Quantity</Label>
                  <div className="text-lg font-semibold mt-1">{totals.quantity}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Total Discount</Label>
                  <div className="text-lg font-semibold mt-1">{formatCurrency(totals.discount)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Total SGST</Label>
                  <div className="text-lg font-semibold mt-1">{formatCurrency(totals.sgst)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Total CGST</Label>
                  <div className="text-lg font-semibold mt-1">{formatCurrency(totals.cgst)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Total IGST</Label>
                  <div className="text-lg font-semibold mt-1">{formatCurrency(totals.igst)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Total Cess</Label>
                  <div className="text-lg font-semibold mt-1">{formatCurrency(totals.cess)}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-gray-700">Total Invoiced Amount</Label>
                    <div className="text-2xl font-bold text-blue-600 mt-1">
                      {formatCurrency(totals.total)}
                    </div>
                  </div>
                  
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Overwrite Options */}
      {!isDebitNote && (
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Cost Price Update Option
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="overwrite"
                        name="overwriteOption"
                        value="overwrite"
                        checked={overwriteOption === 'overwrite'}
                        onChange={(e) => setOverwriteOption(e.target.value as 'overwrite' | 'average' | 'maximum')}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <Label htmlFor="overwrite" className="text-sm cursor-pointer">
                        Overwrite Existing
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="average"
                        name="overwriteOption"
                        value="average"
                        checked={overwriteOption === 'average'}
                        onChange={(e) => setOverwriteOption(e.target.value as 'overwrite' | 'average' | 'maximum')}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <Label htmlFor="average" className="text-sm cursor-pointer">
                        Average with Previous
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="maximum"
                        name="overwriteOption"
                        value="maximum"
                        checked={overwriteOption === 'maximum'}
                        onChange={(e) => setOverwriteOption(e.target.value as 'overwrite' | 'average' | 'maximum')}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <Label htmlFor="maximum" className="text-sm cursor-pointer">
                        Maximum of Costprice
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer Actions */}
      <div className={cn(
        'sticky bottom-0 bg-white border-t p-4',
        isMobile && 'pb-safe'
      )}>
        <div className="flex gap-3 max-w-7xl mx-auto">
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex-1 sm:flex-initial sm:min-w-[120px]"
          >
            Close
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 sm:flex-initial sm:min-w-[120px]"
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Serial Number Entry Modal */}
      <SerialNumberEntryModal
        isOpen={isSerialModalOpen}
        onClose={() => {
          setIsSerialModalOpen(false);
          setCurrentProductForSerial(null);
        }}
        onAddToList={handleSerialNumbersAdded}
        productName={currentProductForSerial?.Title || ''}
        barcode={currentProductForSerial?.Barcode || ''}
        productCode={currentProductForSerial?.Code}
        token={token}
        productRate={currentProductForSerial?.MRP || currentProductForSerial?.CostPrice || 0}
        isDebitNote={isDebitNote}
        locationId={finalLocationId}
        hideCostPrice
        defaultExpiryForever={expiryForeverDefault}
      />

      {/* Add New Product Modal */}
      <Dialog open={showAddProductModal} onOpenChange={setShowAddProductModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
            <DialogDescription>
              Fill in the details to add a new product to your inventory.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ProductEntryForm
              token={token}
              showFooter={false}
              onSuccess={(productData) => {
                setShowAddProductModal(false);
                // Refresh the products query to include the new product
                queryClient.invalidateQueries({ queryKey: ['products-name'] });
                queryClient.invalidateQueries({ queryKey: ['products-barcode'] });
                
                // Add the new product to the table
                if (productData) {
                  // Check if the product has serial numbers
                  if (productData.hasSerial === true || productData.hasSerial === 1) {
                    // Show serial number modal for this product
                    const serialProduct = {
                      Title: productData.Title || '',
                      Barcode: productData.Barcode || '',
                      Code: productData.Code || 0,
                      HSN: productData.HSN || '',
                      CostPrice: parseFloat(productData.CostPrice || '0') || 0,
                      MRP: parseFloat(productData.MRP || productData.CostPrice || '0') || 0,
                      Available: productData.Available || '0',
                      PartNo: productData.PartNo || '',
                      hasserial: 1,
                      hasSerial: 1,
                      SgstPer: productData.SgstPer || 0,
                      CGstPer: productData.CGstPer || 0,
                      IgstPer: productData.IgstPer || 0,
                      CessPer: productData.CessPer || 0,
                      DiscountPer: productData.DiscountPer || 0,
                    };
                    
                    setCurrentProductForSerial(serialProduct);
                    setIsSerialModalOpen(true);
                    
                    toast({
                      title: 'Product Added Successfully',
                      description: `${productData.Title} requires serial numbers. Please enter them.`,
                    });
                  } else {
                    // Add product directly to table (no serial numbers)
                    const sgstPer = isSameState ? parsePercent(productData.SgstPer, 0) : 0;
                    const cgstPer = isSameState ? parsePercent(productData.CGstPer, 0) : 0;
                    const igstPer = isSameState ? 0 : parsePercent(productData.IgstPer, 0);
                    
                    const newProduct: Product = {
                      id: `product-${Date.now()}-${Math.random()}`,
                      productName: productData.Title || '',
                      barcode: productData.Barcode || '',
                      code: productData.Code || 0,
                      hsnCode: productData.HSN || '',
                      unitCost: parseFloat(productData.CostPrice || '0') || 0,
                      quantity: 1,
                      discountPer: parsePercent(productData.DiscountPer, 0),
                      discountAmount: 0,
                      sgstPer,
                      sgstAmount: 0,
                      cgstPer,
                      cgstAmount: 0,
                      igstPer,
                      igstAmount: 0,
                      cessPer: parsePercent(productData.CessPer, 0),
                      cessAmount: 0,
                      totalCost: 0,
                      availableQuantity: parseFloat(productData.Available || '0') || 0,
                      invoiceQuantity: 0,
                      partNo: productData.PartNo || '',
                      indentNo: indentNo,
                      hasSerial: false,
                      serialNumbers: [],
                    };

                    const calculatedProduct = calculateProductValues(newProduct);
                    setProducts(prev => [...prev, calculatedProduct]);
                    
                    toast({
                      title: 'Product Added Successfully',
                      description: `${productData.Title} has been added to the invoice.`,
                    });
                  }
                } else {
                  toast({
                    title: 'Product Added Successfully',
                    description: 'The new product has been added to your inventory.',
                  });
                }
              }}
              onCancel={() => setShowAddProductModal(false)}
              ref={productFormRef}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddProductModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="product-form"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => productFormRef.current?.submit()}
            >
              Add Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Serial Popup (Debit process only) – single or table by count */}
      <Dialog open={showSerialPopup} onOpenChange={(open) => {
        if (!open) return;
      }}>
        <DialogContent
          className={deletedSerials.length > 1 ? 'sm:max-w-[90vw] max-w-lg max-h-[90vh] overflow-hidden flex flex-col' : 'sm:max-w-[500px] max-h-[90vh] overflow-y-auto'}
          hideCloseButton
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Get Serial Number Details
            </DialogTitle>
            <DialogDescription>
              {deletedSerials.length > 1
                ? `${deletedSerials.length} serial numbers were deleted during the debit process. Please fill the details and create them.`
                : 'A serial number was deleted during the debit process. Please fill the details and create it.'}
            </DialogDescription>
          </DialogHeader>

          {/* Table-based popup for multiple deleted serials */}
          {deletedSerials.length > 1 && (
            <div className="py-4 flex flex-col min-h-0 animate-in fade-in duration-200">
              <div className="overflow-x-auto overflow-y-auto flex-1 rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 sticky top-0 z-10">
                      <TableHead className="whitespace-nowrap font-medium">Serial Number</TableHead>
                      <TableHead className="whitespace-nowrap font-medium">MFG Date <span className="text-red-500">*</span></TableHead>
                      <TableHead className="whitespace-nowrap font-medium">Batch No</TableHead>
                      <TableHead className="whitespace-nowrap font-medium">Forever</TableHead>
                      <TableHead className="whitespace-nowrap font-medium">Expiry Days</TableHead>
                      <TableHead className="whitespace-nowrap font-medium">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedSerials.map((serial, i) => (
                      <TableRow key={`${serial.serialNumber}-${i}`} className={i % 2 === 1 ? 'bg-muted/30' : ''}>
                        <TableCell className="align-middle">
                          <Input
                            value={serial.serialNumber}
                            readOnly
                            className="h-9 font-mono bg-muted"
                          />
                        </TableCell>
                        <TableCell className="align-middle">
                          <Input
                            type="date"
                            value={serialTableFormData[i]?.mfgDate ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSerialTableFormData(prev => {
                                const next = [...prev];
                                if (next[i]) next[i] = { ...next[i], mfgDate: v };
                                return next;
                              });
                            }}
                            className="h-9 min-w-[120px]"
                          />
                        </TableCell>
                        <TableCell className="align-middle">
                          <Input
                            value={serialTableFormData[i]?.batchNo ?? '0'}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSerialTableFormData(prev => {
                                const next = [...prev];
                                if (next[i]) next[i] = { ...next[i], batchNo: v };
                                return next;
                              });
                            }}
                            className="h-9 w-20"
                          />
                        </TableCell>
                        <TableCell className="align-middle">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={serialTableFormData[i]?.forever ?? expiryForeverDefault}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSerialTableFormData(prev => {
                                  const next = [...prev];
                                  if (next[i]) {
                                    next[i] = {
                                      ...next[i],
                                      forever: checked,
                                      expiryDays: checked ? '999999' : '0',
                                    };
                                  }
                                  return next;
                                });
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="align-middle">
                          <Input
                            type="number"
                            min={0}
                            value={serialTableFormData[i]?.expiryDays ?? (expiryForeverDefault ? '999999' : '0')}
                            readOnly={serialTableFormData[i]?.forever ?? expiryForeverDefault}
                            disabled={serialTableFormData[i]?.forever ?? expiryForeverDefault}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSerialTableFormData(prev => {
                                const next = [...prev];
                                if (next[i]) next[i] = { ...next[i], expiryDays: v };
                                return next;
                              });
                            }}
                            className="h-9 w-24"
                          />
                        </TableCell>
                        <TableCell className="align-middle">
                          <Input
                            type="number"
                            step="0.01"
                            value={serialTableFormData[i]?.rate ?? '0'}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSerialTableFormData(prev => {
                                const next = [...prev];
                                if (next[i]) next[i] = { ...next[i], rate: v };
                                return next;
                              });
                            }}
                            className="h-9 w-24"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DialogFooter className="sm:justify-center pt-4 shrink-0">
                <Button
                  type="button"
                  onClick={handleCreateSerialMultiple}
                  disabled={isSubmittingSerials}
                  className="bg-teal-600 hover:bg-teal-700 text-white min-w-[120px]"
                >
                  {isSubmittingSerials ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Add'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}

          {deletedSerialInfo && deletedSerials.length <= 1 && (
            <div className="py-4 space-y-4">
              {/* Pre-filled Serial Number only (Product ID not shown in UI) */}
              <div>
                <Label className="text-sm font-medium text-gray-700">Serial Number</Label>
                <div className="mt-1 text-lg font-mono font-semibold text-foreground">{deletedSerialInfo.serialNumber}</div>
              </div>

              {/* Form fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-mfgDate" className="text-sm font-medium text-gray-700">
                      MFG Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="create-mfgDate"
                      type="date"
                      value={serialFormData.mfgDate}
                      onChange={(e) => setSerialFormData(prev => ({ ...prev, mfgDate: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="create-rate" className="text-sm font-medium text-gray-700">
                      Rate
                    </Label>
                    <Input
                      id="create-rate"
                      type="number"
                      step="0.01"
                      value={serialFormData.rate}
                      onChange={(e) => setSerialFormData(prev => ({ ...prev, rate: e.target.value }))}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-batchNo" className="text-sm font-medium text-gray-700">
                      Batch No
                    </Label>
                    <Input
                      id="create-batchNo"
                      type="text"
                      value={serialFormData.batchNo}
                      onChange={(e) => setSerialFormData(prev => ({ ...prev, batchNo: e.target.value }))}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="create-forever"
                        checked={serialFormData.forever}
                        onChange={(e) => {
                          const forever = e.target.checked;
                          setSerialFormData(prev => ({
                            ...prev,
                            forever,
                            expiryDays: forever ? '999999' : '0'
                          }));
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
                      />
                      <Label htmlFor="create-forever" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Forever
                      </Label>
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="create-expiryDays" className="text-sm font-medium text-gray-700">
                        Expiry Days
                      </Label>
                      <Input
                        id="create-expiryDays"
                        type="number"
                        min={0}
                        value={serialFormData.expiryDays}
                        readOnly={serialFormData.forever}
                        disabled={serialFormData.forever}
                        onChange={(e) => setSerialFormData(prev => ({ ...prev, expiryDays: e.target.value }))}
                        className="mt-1"
                        placeholder={serialFormData.forever ? '999999' : '0'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {deletedSerialInfo && deletedSerials.length <= 1 && (
            <DialogFooter className="sm:justify-center pt-4">
              <Button
                type="button"
                onClick={handleCreateSerial}
                className="bg-teal-600 hover:bg-teal-700 text-white min-w-[120px]"
              >
                Add
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierInvoiceProductEntry;
